import {
  EXPORT_BACKEND_TECH_STACK_HEADER,
  EXPORT_FRONTEND_TECH_STACK_HEADER,
} from './productMasterTechStackSummary';

/** v3: single JIIMA Certification Expiry optional column (replaces four per-type columns). */
export const PRODUCT_MASTER_OPTIONAL_COLUMNS_STORAGE_KEY =
  'dfp-product-master-optional-columns-v3';

/** Prior storage key; read when v3 is unset so legacy JIIMA toggles migrate in {@link mergeOptionalColumnVisibility}. */
export const PRODUCT_MASTER_OPTIONAL_COLUMNS_STORAGE_KEY_LEGACY_V2 =
  'dfp-product-master-optional-columns-v2';

/** v2 optional-column keys merged into {@link ProductMasterOptionalStaticColumnKey} `jiimaCertificationExpiry`. */
const LEGACY_JIIMA_OPTIONAL_COLUMN_KEYS = [
  'jiimaScannerPreservation',
  'jiimaElectronicAccounting',
  'jiimaElectronicCommerce',
  'jiimaElectronicDocument',
] as const;

export const DEV_ROLE_COLUMN_PREFIX = 'devRole:';
export const BIZ_ROLE_COLUMN_PREFIX = 'bizRole:';

/**
 * Preferred Dev optional-column order. Titles match backend aggregation: kebab-case group
 * role keys are turned into Title Case words (e.g. `lead-qa` → `Lead Qa`, `lead-pdm` → `Lead Pdm`,
 * `head-of-development` → `Head Of Development`).
 */
export const PRODUCT_MASTER_DEV_ROLE_COLUMN_ORDER = [
  'Lead Qa',
  'Lead Designer',
  'Lead Pdm',
  'Lead Engineer',
  'Head Of Development',
  'Responsible Pic',
] as const;

/**
 * Preferred Biz optional-column order (unknown roles sort alphabetically after these).
 */
export const PRODUCT_MASTER_BIZ_ROLE_COLUMN_ORDER = [
  'Business Manager',
  'Sales Manager',
  'Customer Success Manager',
  'Customer Support Manager',
] as const;

/** Optional columns shown after Product Code and before Product Category. */
export const OPTIONAL_STATIC_KEYS_BEFORE_CATEGORY = [
  'productNameEn',
  'productNameJp',
  'productDescription',
] as const;

/** Optional column shown immediately after Lifecycle Status. */
export const OPTIONAL_STATIC_KEYS_AFTER_LIFECYCLE = ['productId'] as const;

/**
 * Optional columns after Lifecycle Status: surface type, core, infra, documentation,
 * catalog metadata links, relations, code mappings, regulations, JIIMA, and record dates.
 * Entries 1–3 are core contacts (rendered before Dev/Biz role columns).
 */
export const OPTIONAL_STATIC_KEYS_AFTER_ACTIVE_STATUS = [
  'surfaceTypes',
  'coreContactOwner',
  'coreContactEmergency',
  'coreContactSre',
  'infrastructureEnvironment',
  'infrastructureAwsAccounts',
  'infrastructureAzureSubscriptions',
  'adminWeb',
  'documentation',
  'metadataLinks',
  'relations',
  'platformMappings',
  'githubRepository',
  'technologyStack',
  'regulationsStandard',
  'regulationsGovernment',
  'jiimaCertificationExpiry',
  'createdAt',
  'updatedAt',
] as const;

/** Fixed optional columns (excluding dynamic dev/biz role columns), storage / select-all order. */
export const PRODUCT_MASTER_OPTIONAL_STATIC_COLUMN_KEYS = [
  ...OPTIONAL_STATIC_KEYS_BEFORE_CATEGORY,
  ...OPTIONAL_STATIC_KEYS_AFTER_LIFECYCLE,
  ...OPTIONAL_STATIC_KEYS_AFTER_ACTIVE_STATUS,
] as const;

export type ProductMasterOptionalStaticColumnKey =
  (typeof PRODUCT_MASTER_OPTIONAL_STATIC_COLUMN_KEYS)[number];

/** Single table/export column title for combined JIIMA expiry rows. */
export const JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER =
  'JIIMA Certification Expiry';

/** Detail keys → export headers (must match `PRODUCT_MASTER_DETAIL_EXPORT_HEADERS` in productMasterExport). */
export const EXPORT_DETAIL_HEADER_ENVIRONMENT = 'Infrastructure Environment';
export const EXPORT_DETAIL_HEADER_AWS_PROD = 'AWS Production Account';
export const EXPORT_DETAIL_HEADER_AWS_STAGING = 'AWS Test Account';
export const EXPORT_DETAIL_HEADER_AZURE_PROD = 'Azure Production Subscription';
export const EXPORT_DETAIL_HEADER_AZURE_STAGING = 'Azure Test Subscription';
export const EXPORT_DETAIL_HEADER_ADMIN_WEB_PROD = 'Admin Web — Prod';
export const EXPORT_DETAIL_HEADER_ADMIN_WEB_STAGING = 'Admin Web — Staging';
export const EXPORT_DETAIL_HEADER_ADMIN_WEB_WORKFLOW = 'Admin Web — Workflow';

export const EXPORT_RELATIONS_HEADER = 'Relations';
export const EXPORT_GITHUB_FRONTEND_HEADER = 'GitHub frontend repository';
export const EXPORT_GITHUB_BACKEND_HEADER = 'GitHub backend repository';

/** One row per software type (table + CSV cell); `specField` matches aggregated `jiimaCertification` keys. */
export const JIIMA_CERTIFICATION_LINE_DEFS: ReadonlyArray<{
  specField: string;
  lineLabel: string;
}> = [
  {
    specField: 'certification_of_scanner_preservation_software',
    lineLabel: 'Scanner Preservation Software',
  },
  {
    specField: 'certification_of_electronic_accounting_software',
    lineLabel: 'Electronic Accounting Software',
  },
  {
    specField: 'certification_of_electronic_commerce_software',
    lineLabel: 'Electronic Commerce Software',
  },
  {
    specField: 'certification_of_electronic_document_software',
    lineLabel: 'Electronic Document Software',
  },
];

export const STANDARD_REGULATION_DEFS: ReadonlyArray<{
  key: string;
  label: string;
}> = [
  { key: 'soc1', label: 'SOC1' },
  { key: 'soc2', label: 'SOC2' },
  { key: 'jsox', label: 'J-SOX' },
];

/**
 * Single source of truth for the Admin Web rows rendered in:
 *   - the product master table optional column ("Admin Web" cell)
 *   - the product details Infrastructure card ("Admin Web" list)
 *   - the edit/create product Infrastructure form
 * `key` matches the aggregated product master detail field; `label` is the
 * row label shown in UI; `annotation` is the corresponding `metadata.annotations`
 * key in product.yaml.
 */
export const ADMIN_WEB_ROW_DEFS: ReadonlyArray<{
  key: 'adminWebProdUrl' | 'adminWebStagingUrl' | 'awebWorkflowUrl';
  label: string;
  annotation: 'admin.web/prod' | 'admin.web/staging' | 'admin.web/workflow';
  /** Tooltip / aria title used on the external-link icon. */
  linkTitle: string;
}> = [
  {
    key: 'adminWebProdUrl',
    label: 'Prod',
    annotation: 'admin.web/prod',
    linkTitle: 'Admin web (production)',
  },
  {
    key: 'adminWebStagingUrl',
    label: 'Staging',
    annotation: 'admin.web/staging',
    linkTitle: 'Admin web (staging)',
  },
  {
    key: 'awebWorkflowUrl',
    label: 'Workflow',
    annotation: 'admin.web/workflow',
    linkTitle: 'Admin web workflow',
  },
];

export const GOVERNMENT_REGULATION_DEFS: ReadonlyArray<{
  key: string;
  label: string;
}> = [
  {
    key: 'telecommunications_business_law',
    label: 'Telecommunications Business Law',
  },
  { key: 'electronic_payment_agency', label: 'Electronic Payment Agency' },
  {
    key: 'prepaid_payment_instruments_issuers',
    label: 'Prepaid Payment Instruments Issuers',
  },
  {
    key: 'funds_transfer_service_providers',
    label: 'Funds Transfer Service Providers',
  },
  {
    key: 'financial_services_intermediary_business',
    label: 'Financial Services Intermediary Business',
  },
  {
    key: 'subcontractor_management_required',
    label: 'Subcontractor Management Required',
  },
  {
    key: 'personal_information_handling',
    label: 'Personal Information Handling',
  },
  { key: 'need_to_follow_standards', label: 'Need to follow standards' },
];

/**
 * Minimal bundle shape for union role extraction (keeps this module independent of
 * `productMasterExport` so export can import column order here without a circular dependency).
 */
export interface ProductMasterExportBundleLike {
  /** Optional; ignored by union-role helpers but useful in tests and bundle lists. */
  productCode?: string;
  productMasterDetails?: Record<string, unknown>;
  error?: string;
}

export function buildDefaultOptionalColumnVisibility(): Record<
  string,
  boolean
> {
  const out: Record<string, boolean> = {};
  PRODUCT_MASTER_OPTIONAL_STATIC_COLUMN_KEYS.forEach(k => {
    out[k] = false;
  });
  return out;
}

function contactRoleLabel(entry: unknown): string | undefined {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return undefined;
  }
  const role = (entry as { role?: unknown }).role;
  return typeof role === 'string' && role.trim() ? role.trim() : undefined;
}

function sortRolesWithPreferredOrder(
  roles: string[],
  preferred: readonly string[],
): string[] {
  const indexOfCanonical = new Map<string, number>();
  const lowerToCanonical = new Map<string, string>();
  preferred.forEach((label, i) => {
    indexOfCanonical.set(label, i);
    lowerToCanonical.set(label.trim().toLowerCase(), label);
  });
  const sortKey = (r: string): { tier: number; label: string } => {
    const t = r.trim();
    let tier = indexOfCanonical.get(t);
    if (tier === undefined) {
      const canon = lowerToCanonical.get(t.toLowerCase());
      if (canon !== undefined) {
        tier = indexOfCanonical.get(canon);
      }
    }
    if (tier === undefined) {
      return { tier: Number.MAX_SAFE_INTEGER, label: t };
    }
    return { tier, label: t };
  };
  return [...roles].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka.tier !== kb.tier) {
      return ka.tier - kb.tier;
    }
    return ka.label.localeCompare(kb.label);
  });
}

export function collectUnionContactRoles(
  bundles: ProductMasterExportBundleLike[],
  field: 'devContacts' | 'bizContacts',
  preferredOrder?: readonly string[],
): string[] {
  const set = new Set<string>();
  for (const b of bundles) {
    if (b.error) {
      continue;
    }
    const arr = b.productMasterDetails?.[field];
    if (!Array.isArray(arr)) {
      continue;
    }
    for (const row of arr) {
      const label = contactRoleLabel(row);
      if (label) {
        set.add(label);
      }
    }
  }
  const labels = Array.from(set);
  if (!preferredOrder?.length) {
    return labels.sort((a, b) => a.localeCompare(b));
  }
  return sortRolesWithPreferredOrder(labels, preferredOrder);
}

export function collectUnionDevRoles(
  bundles: ProductMasterExportBundleLike[],
): string[] {
  return collectUnionContactRoles(
    bundles,
    'devContacts',
    PRODUCT_MASTER_DEV_ROLE_COLUMN_ORDER,
  );
}

export function collectUnionBizRoles(
  bundles: ProductMasterExportBundleLike[],
): string[] {
  return collectUnionContactRoles(
    bundles,
    'bizContacts',
    PRODUCT_MASTER_BIZ_ROLE_COLUMN_ORDER,
  );
}

/**
 * Build the role list for optional Dev/Biz columns: always include standard roles from
 * `preferred` (so the column menu can render toggles before detail bundles load), then add
 * any extra roles seen in loaded bundles (rare / unknown labels).
 */
export function mergePreferredAndDiscoveredContactRoles(
  preferred: readonly string[],
  discovered: string[],
): string[] {
  const merged = new Set<string>();
  for (const r of preferred) {
    merged.add(r);
  }
  for (const r of discovered) {
    if (typeof r === 'string' && r.trim()) {
      merged.add(r);
    }
  }
  return sortRolesWithPreferredOrder([...merged], preferred);
}

function legacyJiimaVisibleFromRaw(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const persisted = raw as Record<string, unknown>;
  return LEGACY_JIIMA_OPTIONAL_COLUMN_KEYS.some(k => persisted[k] === true);
}

function applyPersistedBooleanKeys(
  out: Record<string, boolean>,
  allowed: Set<string>,
  raw: Record<string, unknown>,
): void {
  for (const [key, val] of Object.entries(raw)) {
    if (allowed.has(key) && typeof val === 'boolean') {
      out[key] = val;
    }
  }
}

function hasExplicitJiimaCertificationExpiryFlag(raw: unknown): boolean {
  return Boolean(
    raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      typeof (raw as Record<string, unknown>).jiimaCertificationExpiry ===
        'boolean',
  );
}

function hasExplicitPlatformMappingsFlag(raw: unknown): boolean {
  return Boolean(
    raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      typeof (raw as Record<string, unknown>).platformMappings === 'boolean',
  );
}

function legacyProductCodeMappingsColumnVisible(raw: unknown): boolean {
  return Boolean(
    raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      (raw as Record<string, unknown>).productCodeMappings === true,
  );
}

/**
 * Allowed keys = static + devRole:${label} + bizRole:${label}.
 * Reads persisted booleans; unknown keys ignored; missing keys default false.
 */
export function mergeOptionalColumnVisibility(
  raw: unknown,
  unionDevRoles: string[],
  unionBizRoles: string[],
): Record<string, boolean> {
  const out = buildDefaultOptionalColumnVisibility();
  unionDevRoles.forEach(r => {
    out[`${DEV_ROLE_COLUMN_PREFIX}${r}`] = false;
  });
  unionBizRoles.forEach(r => {
    out[`${BIZ_ROLE_COLUMN_PREFIX}${r}`] = false;
  });

  const allowed = new Set(Object.keys(out));
  const legacyJiimaVisible = legacyJiimaVisibleFromRaw(raw);

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    applyPersistedBooleanKeys(out, allowed, raw as Record<string, unknown>);
  }

  if (
    legacyProductCodeMappingsColumnVisible(raw) &&
    !hasExplicitPlatformMappingsFlag(raw)
  ) {
    out.platformMappings = true;
  }

  if (legacyJiimaVisible && !hasExplicitJiimaCertificationExpiryFlag(raw)) {
    out.jiimaCertificationExpiry = true;
  }
  return out;
}

export function getAllOptionalColumnKeys(
  unionDevRoles: string[],
  unionBizRoles: string[],
): string[] {
  return [
    ...PRODUCT_MASTER_OPTIONAL_STATIC_COLUMN_KEYS,
    ...unionDevRoles.map(r => `${DEV_ROLE_COLUMN_PREFIX}${r}`),
    ...unionBizRoles.map(r => `${BIZ_ROLE_COLUMN_PREFIX}${r}`),
  ];
}

const STATIC_MENU_LABELS: Record<ProductMasterOptionalStaticColumnKey, string> =
  {
    productId: 'Product ID',
    productNameEn: 'Product Name (EN)',
    productNameJp: 'Product Name (JP)',
    productDescription: 'Product Description',
    surfaceTypes: 'Surface Type',
    documentation: 'Documentation (TechDocs)',
    metadataLinks: 'Links',
    relations: 'Relations',
    coreContactOwner: 'Core contact — Owner',
    coreContactEmergency: 'Core contact — Emergency',
    coreContactSre: 'Core contact — SRE',
    infrastructureEnvironment: 'Infrastructure — Environment',
    infrastructureAwsAccounts: 'Infrastructure — AWS accounts (prod & staging)',
    infrastructureAzureSubscriptions:
      'Infrastructure — Azure subscriptions (prod & staging)',
    adminWeb: 'Admin Web (prod, staging & workflow URLs)',
    regulationsStandard: 'Regulations — Standard (SOC / J-SOX)',
    regulationsGovernment: 'Regulations — Government',
    jiimaCertificationExpiry: 'JIIMA Certification Expiry',
    platformMappings: 'Platform Mappings',
    githubRepository: 'Github Repository',
    technologyStack: 'Technology Stack',
    createdAt: 'Created Date',
    updatedAt: 'Last Updated Date',
  };

export const STATIC_TABLE_HEADER: Record<
  ProductMasterOptionalStaticColumnKey,
  string
> = {
  productId: 'Product ID',
  productNameEn: 'Product Name (EN)',
  productNameJp: 'Product Name (JP)',
  productDescription: 'Product Description',
  surfaceTypes: 'Surface Type',
  documentation: 'Documentation',
  metadataLinks: 'Links',
  relations: 'Relations',
  coreContactOwner: 'Core —  Owner',
  coreContactEmergency: 'Core —  Emergency',
  coreContactSre: 'Core —  SRE',
  infrastructureEnvironment: 'Infra — Environment',
  infrastructureAwsAccounts: 'Infra — AWS Accounts',
  infrastructureAzureSubscriptions: 'Infra — Azure Subscriptions',
  adminWeb: 'Admin Web',
  regulationsStandard: 'Regulations',
  regulationsGovernment: 'Government Regulations',
  jiimaCertificationExpiry: JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER,
  platformMappings: 'Platform Mappings',
  githubRepository: 'Github Repository',
  technologyStack: 'Technology Stack',
  createdAt: 'Created Date',
  updatedAt: 'Last Updated Date',
};

/**
 * CSV / JSON column order aligned with `ProductMasterComponent` table column sequence
 * (excluding Favourite). Core contacts export as combined columns; Dev/Biz match per-role
 * table headers (`Dev — …` / `Biz — …`). JIIMA is one combined column. Infra uses the same
 * headers as detail export keys (`Infrastructure Environment` before `AWS Production Account`, …).
 * Tail: Links, Relations, product code mappings, regulations, JIIMA, record dates, then
 * `Export error`.
 * Keys not present in a row are skipped; extras sort alphabetically.
 * List header strings must stay in sync with `PRODUCT_MASTER_TABLE_LIST_HEADERS` in
 * `productMasterExport.ts`. `Export error` must stay in sync with `EXPORT_ERROR_HEADER`.
 */
export function buildProductMasterExportColumnOrder(
  unionDevRoles: readonly string[],
  unionBizRoles: readonly string[],
): readonly string[] {
  return [
    'Product Code',
    ...OPTIONAL_STATIC_KEYS_BEFORE_CATEGORY.map(k => STATIC_TABLE_HEADER[k]),
    'Product Category',
    'Segment/Company',
    'Domain',
    'Division/Department',
    'Lifecycle Status',
    ...OPTIONAL_STATIC_KEYS_AFTER_LIFECYCLE.map(k => STATIC_TABLE_HEADER[k]),
    STATIC_TABLE_HEADER.surfaceTypes,
    'Core Owner Contact',
    'Core Emergency Contact',
    'Core SRE Contact',
    ...unionDevRoles.map(r => `Dev — ${r}`),
    ...unionBizRoles.map(r => `Biz — ${r}`),
    EXPORT_DETAIL_HEADER_ENVIRONMENT,
    EXPORT_DETAIL_HEADER_AWS_PROD,
    EXPORT_DETAIL_HEADER_AWS_STAGING,
    EXPORT_DETAIL_HEADER_AZURE_PROD,
    EXPORT_DETAIL_HEADER_AZURE_STAGING,
    EXPORT_DETAIL_HEADER_ADMIN_WEB_PROD,
    EXPORT_DETAIL_HEADER_ADMIN_WEB_STAGING,
    EXPORT_DETAIL_HEADER_ADMIN_WEB_WORKFLOW,
    STATIC_TABLE_HEADER.documentation,
    STATIC_TABLE_HEADER.metadataLinks,
    EXPORT_RELATIONS_HEADER,
    STATIC_TABLE_HEADER.platformMappings,
    EXPORT_GITHUB_FRONTEND_HEADER,
    EXPORT_FRONTEND_TECH_STACK_HEADER,
    EXPORT_GITHUB_BACKEND_HEADER,
    EXPORT_BACKEND_TECH_STACK_HEADER,
    STATIC_TABLE_HEADER.regulationsStandard,
    STATIC_TABLE_HEADER.regulationsGovernment,
    JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER,
    STATIC_TABLE_HEADER.createdAt,
    STATIC_TABLE_HEADER.updatedAt,
    'Export error',
  ];
}

export function getOptionalColumnMenuLabel(columnKey: string): string {
  if (columnKey.startsWith(DEV_ROLE_COLUMN_PREFIX)) {
    return `Dev — ${columnKey.slice(DEV_ROLE_COLUMN_PREFIX.length)}`;
  }
  if (columnKey.startsWith(BIZ_ROLE_COLUMN_PREFIX)) {
    return `Biz — ${columnKey.slice(BIZ_ROLE_COLUMN_PREFIX.length)}`;
  }
  if (columnKey in STATIC_MENU_LABELS) {
    return STATIC_MENU_LABELS[
      columnKey as ProductMasterOptionalStaticColumnKey
    ];
  }
  return columnKey;
}
